import { uniqBy } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';

import { AppliedFiltersChipsCollectedData } from '~components/Filters/AppliedFiltersChips';
import { FilterMenuOption } from '~components/Filters/FilterMenuItem';
import { UserSelectedFilter } from '~constants/enums';

export type Filters = {
  roles?: string[];
  labels?: string[];
};

type UserFilterKey = Extract<keyof Filters, 'roles' | 'labels'>;

type AppliedFiltersState = Record<
  UserSelectedFilter,
  {
    userFilterKey: UserFilterKey;
    search: string;
  }
>;

export const userFilterKeyBySelectedFilter: Record<UserSelectedFilter, UserFilterKey> = {
  [UserSelectedFilter.ROLES]: 'roles',
  [UserSelectedFilter.LABELS]: 'labels',
};

type FiltersAvailableOptionsState = Record<UserSelectedFilter, FilterMenuOption[]>;

const getAppliedFiltersInitialState = () => {
  const initialState = Object.entries(userFilterKeyBySelectedFilter).reduce(
    (obj, [filterKey, storeKey]) => {
      const key = filterKey as UserSelectedFilter;

      obj[key] = {
        userFilterKey: storeKey,
        search: '',
      };

      return obj;
    },
    {} as AppliedFiltersState,
  );

  return initialState;
};

const getAppliedFiltersChipsCollectedDataInitialState = (
  availableOptions: FiltersAvailableOptionsState,
) => {
  const collectedData = Object.entries(availableOptions).reduce((obj, [key, options]) => {
    obj[key as UserSelectedFilter] = options;

    return obj;
  }, {} as AppliedFiltersChipsCollectedData);

  return collectedData;
};

const AVAILABLE_OPTIONS_STORAGE_KEY = 'user_availableOptions';
type AppliedFiltersStoredAvailableOptions = Partial<
  Record<UserSelectedFilter, FilterMenuOption[]>
>;

const getAvailableOptionsInitialState = () => {
  const stored = localStorage.getItem(AVAILABLE_OPTIONS_STORAGE_KEY);
  const parsed = (
    stored ? JSON.parse(stored) : {}
  ) as AppliedFiltersStoredAvailableOptions;

  const initialState = {
    ...Object.values(UserSelectedFilter).reduce((obj, filter) => {
      obj[filter] = parsed[filter] ?? [];

      return obj;
    }, {} as FiltersAvailableOptionsState),
  };

  return initialState;
};

export default function useUserFilters() {
  const [appliedFilters, setAppliedFilters] = useState(getAppliedFiltersInitialState());
  const [availableOptions, setAvailableOptions] = useState(
    getAvailableOptionsInitialState(),
  );

  const [appliedFilterChipsCollectedData, setAppliedFilterChipsCollectedData] = useState(
    getAppliedFiltersChipsCollectedDataInitialState(availableOptions),
  );

  const updateAppliedFilterChipsCollectedData = useCallback(
    (filter: string, data: FilterMenuOption[]) => {
      const isValidFilter = userFilterKeyBySelectedFilter[filter as UserSelectedFilter];

      if (isValidFilter) {
        setAppliedFilterChipsCollectedData((state) => ({
          ...state,
          [filter]: uniqBy([...(state[filter] ?? []), ...data], (i) => i.value),
        }));
      }
    },
    [],
  );

  useEffect(() => {
    const options = Object.entries(availableOptions).reduce((obj, [key, options]) => {
      obj[key as UserSelectedFilter] = options;

      return obj;
    }, {} as AppliedFiltersStoredAvailableOptions);

    localStorage.setItem(AVAILABLE_OPTIONS_STORAGE_KEY, JSON.stringify(options));
  }, [JSON.stringify(availableOptions)]);

  return {
    availableOptions,
    updateAvailableOptions: setAvailableOptions,
    appliedFilters,
    updateAppliedFilters: setAppliedFilters,
    appliedFilterChipsCollectedData,
    updateAppliedFilterChipsCollectedData,
  };
}
